import { getJBPPartnerBasicInfoCrudWithKey } from 'legacy/lut/services/partner360/configuration/helpers/getJBPPartnerBasicInfoCrudWithKey'

import { Logic, Operator } from 'core-components/types'
import type { ICompletenessInfo } from 'partner360/entities/partnerCompleteness'

import type { AcknowledgmentOffering } from '../model/types'

import { recordAcknowledgmentCall } from './recordAcknowledgmentCall'

type FetchAcknowledgmentPartnerInfoParams = {
  partnerId: string
  fYear: number
  programType: AcknowledgmentOffering
  debugSource?: string | null
}

export type AcknowledgmentPartnerInfo = {
  /** Reused by the plan review header; read with the existing partner record. */
  partnerName?: string
  isDistributor?: boolean
  /** The basic info record the plan PDF is attached to when it is generated in the browser. */
  entryId?: number
  isGlobal: boolean
  /** Shown before the partner's side signs or refuses, as the partner page's widget does. */
  warningMessage: string
}

const parseCompleteness = (value?: string | null): ICompletenessInfo[] => {
  try {
    return JSON.parse(value || '[]') as ICompletenessInfo[]
  } catch {
    return []
  }
}

/**
 * The partner record the signing steps around the acknowledgment read: the dialog is opened for
 * partners other than the page's own, so the record is read for the partner it is opened for.
 */
export const fetchAcknowledgmentPartnerInfo = async ({
  partnerId,
  fYear,
  programType,
  debugSource,
}: FetchAcknowledgmentPartnerInfoParams): Promise<AcknowledgmentPartnerInfo> => {
  const crud = getJBPPartnerBasicInfoCrudWithKey(partnerId, fYear)

  crud.setFilter({
    logic: Logic.AND,
    filters: [
      { field: 'PartnerID', operator: Operator.EQUAL, value: partnerId },
      { field: 'FYear', operator: Operator.EQUAL, value: fYear },
    ],
  })

  return recordAcknowledgmentCall(
    {
      name: 'JBPPartnerBasicInfo',
      method: 'GET',
      url: `CrudStore · JBPPartnerBasicInfo · fetch({ PartnerID: "${partnerId}", FYear: ${fYear} })`,
      body: null,
      target: 'useAcknowledgmentPartnerInfo · partnerInfo',
      source: debugSource,
    },
    () => crud.fetch(),
    ({ items }) => {
      const info = items[0]
      const completeness = parseCompleteness(info?.Completeness)

      return {
        partnerName: info?.PartnerName,
        isDistributor: info?.IsDistributor == null ? undefined : info.IsDistributor === 1,
        entryId: info?.EntryId,
        isGlobal: info?.IsGlobal === 1,
        /* A distributor's acknowledgment covers every offering: any offering's warning stands. */
        warningMessage:
          (programType === null
            ? completeness.find(({ WarningMessage }) => WarningMessage)
            : completeness.find(({ Profile }) => Profile === programType)
          )?.WarningMessage ?? '',
      }
    }
  )
}
